import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { motion } from 'framer-motion';
import DOMPurify from 'dompurify';
import {
  fetchCurrentDisclaimer,
  submitAcceptance,
  clearDisclaimerErrors,
} from '../features/disclaimer/disclaimerSlice';
import DWLFLoader from './DWLFLoader';

// Check if content appears to be HTML by looking for common HTML tag patterns
const isHtmlContent = (content) => {
  if (!content || typeof content !== 'string') return false;
  // Match common HTML tags - more reliable than just checking for '<'
  return /<(p|div|span|br|h[1-6]|ul|ol|li|a|strong|em|b|i|table|tr|td|th|section|article|header|footer)[\s>\/]/i.test(content);
};

const DisclaimerModal = () => {
  const dispatch = useDispatch();
  const [agreed, setAgreed] = useState(false);

  const {
    showModal,
    current,
    currentLoading,
    currentLoaded,
    currentError,
    accepting,
    acceptError,
    status,
  } = useSelector((state) => state.disclaimer);

  const user = useSelector((state) => state.account.user);

  // Fetch disclaimer content when modal opens
  // Use currentLoaded flag to prevent infinite loop when API returns null
  useEffect(() => {
    if (showModal && !currentLoaded && !currentLoading && !currentError) {
      dispatch(fetchCurrentDisclaimer());
    }
  }, [showModal, currentLoaded, currentLoading, currentError, dispatch]);

  // Reset agreed state when modal opens
  useEffect(() => {
    if (showModal) {
      setAgreed(false);
    }
  }, [showModal]);

  const handleAgree = async () => {
    if (!current || !agreed) return;

    dispatch(submitAcceptance({
      version: current.version,
      contentHash: current.contentHash,
      disclaimerType: current.disclaimerType || 'platform',
    }));
  };

  const handleRetry = () => {
    dispatch(clearDisclaimerErrors());
    dispatch(fetchCurrentDisclaimer());
  };

  // Don't render if no user or modal not showing
  if (!showModal || !user) {
    return null;
  }

  const isLoading = currentLoading || accepting;
  const error = currentError || acceptError || status.error;

  return (
    <Dialog
      open={showModal}
      maxWidth="md"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          bgcolor: 'var(--dwlf-elevated)',
          color: 'var(--dwlf-text)',
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: 'var(--dwlf-elevated)',
          borderBottom: '1px solid var(--dwlf-ink-20)',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography variant="h6" component="span">
          {current?.title || 'Platform Disclaimer'}
        </Typography>
        {current?.version && (
          <Typography
            variant="caption"
            sx={{
              bgcolor: 'var(--dwlf-elevated)',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              ml: 1,
            }}
          >
            v{current.version}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {isLoading && !current && (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="300px"
          >
            <DWLFLoader size="medium" />
          </Box>
        )}

        {error && !current && (
          <Box p={3} display="flex" flexDirection="column" alignItems="center" minHeight="200px" justifyContent="center">
            <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
              {error}
            </Alert>
            <Button
              variant="contained"
              onClick={handleRetry}
              disabled={currentLoading}
              sx={{ mt: 2 }}
            >
              {currentLoading ? 'Retrying...' : 'Retry'}
            </Button>
          </Box>
        )}

        {error && current && (
          <Box p={2}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          </Box>
        )}

        {current && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {/* Disclaimer Content */}
            <Box
              sx={{
                p: 3,
                maxHeight: '50vh',
                overflowY: 'auto',
                bgcolor: 'var(--dwlf-elevated)',
                '& p': { mb: 2 },
                '& ul, & ol': { pl: 3, mb: 2 },
                '& li': { mb: 1 },
                '& h1, & h2, & h3, & h4': { mt: 2, mb: 1 },
              }}
            >
              {/* Render content - could be HTML or plain text */}
              {isHtmlContent(current.content) ? (
                <div
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(current.content) }}
                />
              ) : (
                <Typography
                  variant="body1"
                  component="div"
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {current.content}
                </Typography>
              )}
            </Box>

            {/* Agreement Checkbox */}
            <Box sx={{ p: 3, borderTop: '1px solid var(--dwlf-ink-20)' }}>
              {status.acceptedVersion && status.acceptedVersion !== current.version && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  The disclaimer has been updated since you last accepted (v{status.acceptedVersion}).
                  Please review and accept the new version to continue.
                </Alert>
              )}

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    color="primary"
                    disabled={accepting}
                  />
                }
                label={
                  <Typography variant="body2">
                    I have read and agree to the terms outlined in this disclaimer.
                    I understand and accept the risks associated with using this platform.
                  </Typography>
                }
                sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0 } }}
              />
            </Box>
          </motion.div>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: 'var(--dwlf-elevated)',
          borderTop: '1px solid var(--dwlf-ink-20)',
          p: 2,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleAgree}
          disabled={!agreed || accepting || !current}
          sx={{ minWidth: 150 }}
        >
          {accepting ? (
            <>
              <DWLFLoader size="tiny" showText={false} />
              Processing...
            </>
          ) : (
            'I Agree'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DisclaimerModal;
